import SubRouteAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity';
import { Shift, Truck } from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.interface';

/**
 * Resolución de la flota de un grupo de planificación desde el **maestro de
 * sub-ruta**.
 *
 * El negocio ya declaró en `SubRouteAttributes` el camión (`vehicle`), el turno
 * (`itinerary`), las ventanas de entrega (`orderDeliveryWindows`) y si admite
 * recargas (`allowMultiTrips`). Pedirle al optimizador que redescubra la
 * asignación de camión es caro y produce planes que operaciones no ejecuta.
 * Acá se traduce ese maestro al contrato de `POST /plan`.
 *
 * La configuración de pantalla queda como **respaldo explícito**: se usa solo
 * donde el maestro no dice nada, y cada parámetro viaja con su procedencia para
 * que el resultado distinga un plan basado en datos reales de uno basado en
 * supuestos.
 *
 * Módulo puro: entra el maestro, sale el `Truck` del contrato. Sin HTTP.
 */

/** Factor de conversión, mismo criterio que el peso de las órdenes. */
const KG_TO_LB = 2.20462;

/**
 * Último recurso cuando ni el maestro ni la pantalla dan una franja usable.
 * Mejor planificar con una jornada estándar y avisarlo que romper el grupo con
 * un `422` del contrato.
 */
const SAFE_SHIFT: Shift = { start: '08:00', end: '17:00' };

/** De dónde salió un parámetro de flota. */
export type FleetSource = 'master' | 'fallback';

/** Configuración de pantalla que aplica donde el maestro no declara nada. */
export interface FleetFallback {
  /** Capacidad del camión de respaldo, en libras. */
  capacityLb: number;
  /** Franja de entrega de respaldo. */
  shift: Shift;
  /** Modo de viaje elegido en la pantalla. */
  tripMode: 'single' | 'reload';
  /** Techo de viajes cuando el modo es `reload`. */
  maxTrips: number;
  /** Minutos de carga en el almacén entre viajes. */
  reloadMin: number;
}

/** Un camión resuelto, con la procedencia de cada parámetro. */
export interface ResolvedTruck {
  truck: Truck;
  /** Sub-ruta del maestro que lo declara. */
  subRouteId?: string;
  /** De dónde salió la capacidad: del vehículo del maestro, o del respaldo. */
  capacitySource: FleetSource;
  /** De dónde salió la jornada: del itinerario de SU sub-ruta, o del respaldo. */
  shiftSource: FleetSource;
  tripsSource: FleetSource;
}

/** La flota de UN territorio, lista para el `trucks[]` de la petición. */
export interface ResolvedTerritoryFleet {
  territoryId: number;
  territoryName: string;
  /** Vacío = este territorio no se planifica. */
  trucks: ResolvedTruck[];
  /**
   * Ventana de entrega común del territorio, y solo si es **inequívoca**.
   *
   * La ventana se aplica a los PEDIDOS, no al camión, así que con dos sub-rutas
   * del mismo territorio declarando ventanas distintas no hay una respuesta
   * correcta: el pedido lo puede servir cualquiera de los dos camiones. Antes esto
   * no podía pasar (un grupo = una sub-ruta); ahora se resuelve no afirmando nada
   * y avisando, en vez de imponerle a todos la ventana de la primera.
   */
  window?: Shift;
  warnings: string[];
}

export interface ResolveTerritoryFleetInput {
  territoryId: number;
  territoryName: string;
  /** Las unidades del territorio: cada sub-ruta de ENTREGA con su camión. */
  units: readonly { subRoute: SubRouteAttributes; vehicle?: unknown }[];
  /** Día planificado, 1 = lunes … 7 = domingo. */
  weekday: number;
  fallback: FleetFallback;
}

export type VehicleLike = {
  _id?: unknown;
  name?: string;
  plate?: string;
  dimensions?: { grossWeight?: number; weightUnitOfMeasure?: { code?: string } };
};

type ItineraryLike = { days?: number[]; datetimeStart?: Date | string; datetimeEnd?: Date | string };

type DeliveryWindowLike = {
  days?: number[];
  hourStart?: string;
  minutesStart?: number;
  hourEnd?: string;
  minutesEnd?: number;
};

/** `HH:MM` de una fecha, en hora local (que es la que ve el operador). */
function toHhMm(value: Date | string | undefined): string | undefined {
  if (value === undefined || value === null) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** `HH:MM` estricto, la única forma que acepta el contrato de `/plan`. */
const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** Minutos desde medianoche, o `null` si el texto no es un `HH:MM` válido. */
function toMinutes(hhmm: string | undefined): number | null {
  const match = hhmm !== undefined ? HHMM.exec(hhmm) : null;
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/**
 * Un rango horario **usable**, o `undefined`.
 *
 * El contrato de `/plan` valida las dos cosas y responde `422` si fallan: que
 * cada extremo sea `HH:MM` y que `end > start`. El maestro de sub-ruta no
 * garantiza ninguna — un itinerario cuyos `datetimeStart`/`datetimeEnd` caen a
 * la misma hora produce una franja de duración cero, y una ventana mal cargada
 * produce texto que no es hora. Se filtran acá, no en el motor.
 */
function validRange(start: string | undefined, end: string | undefined): Shift | undefined {
  const from = toMinutes(start);
  const to = toMinutes(end);
  if (from === null || to === null || to <= from) return undefined;
  return { start: start as string, end: end as string };
}

/**
 * Compone `HH:MM` a partir de los campos de una ventana del maestro.
 *
 * `orderDeliveryWindows.hourStart` es un **string** y no está normalizado: puede
 * venir como `"9"`, `"09"` o ya con minutos (`"09:30"`). Cuando el propio campo
 * trae minutos mandan esos, porque el campo separado suele quedar en 0.
 */
function composeHhMm(hour: string | undefined, minutes: number | undefined): string | undefined {
  if (hour === undefined || hour === null) return undefined;
  const [rawHour, rawMinute] = String(hour).trim().split(':');
  const h = Number(rawHour);
  if (!rawHour || !Number.isInteger(h) || h < 0 || h > 23) return undefined;
  const m = rawMinute !== undefined && rawMinute !== '' ? Number(rawMinute) : (minutes ?? 0);
  if (!Number.isInteger(m) || m < 0 || m > 59) return undefined;
  return `${pad(h)}:${pad(m)}`;
}

/** Duración de una franja en minutos (0 si está mal formada o invertida). */
function shiftMinutes(shift: Shift): number {
  const from = toMinutes(shift.start);
  const to = toMinutes(shift.end);
  return from === null || to === null ? 0 : Math.max(0, to - from);
}

/** ¿La entrada aplica al día planificado? Sin `days` declarados, aplica siempre. */
function appliesTo(days: number[] | undefined, weekday: number): boolean {
  return !days?.length || days.includes(weekday);
}

/**
 * Capacidad del vehículo normalizada a libras (el maestro mezcla KG y LB).
 *
 * Exportada porque `fleet-sizing` necesita la MISMA conversión para derivar los
 * moldes del dimensionamiento: dos copias del factor y de la regla de unidad
 * divergirían sin dar error, y el síntoma sería una capacidad equivocada en el
 * plan o en la estimación — el tipo de dato con el que se despachan camiones.
 *
 * Ojo con las unidades que NO son KG: se tratan como libras. En la flota real hay
 * un camión con `grossWeight: 4500 MG`, que entra como 4.500 lb. Es un dato a
 * corregir en el maestro, no acá: adivinar la unidad sería peor.
 */
export function capacityLbOf(vehicle: VehicleLike | undefined): number | undefined {
  const raw = vehicle?.dimensions?.grossWeight;
  if (raw === undefined || raw === null || raw <= 0) return undefined;
  const unit = vehicle?.dimensions?.weightUnitOfMeasure?.code?.toUpperCase();
  return unit === 'KG' ? raw * KG_TO_LB : raw;
}

/**
 * Si los dos extremos caen el mismo día calendario.
 *
 * Es lo que separa una **hora del día** de un **rango de fechas**. Medido sobre el
 * maestro de Cortijo, `itinerary.datetimeStart`/`datetimeEnd` traen la vigencia del
 * itinerario, no una franja: 1.116 registros, todos de `2025-01-01` a `2025-12-31`.
 * La UI lo confirma — el formulario usa un `matDatepicker` y la tabla lo muestra con
 * `FormatDateHour: false`, sin hora.
 *
 * Sin esta regla, un año de vigencia se convierte en una franja inventada a partir de
 * sus horas sueltas, y el resultado depende del huso horario del navegador: en UTC-6
 * salía invertido por un minuto y se descartaba por casualidad, pero en UTC daría
 * 00:00–23:59 (1.439 min) y el motor planificaría como si el camión trabajara día y
 * noche. Una franja de una jornada no cruza la medianoche, así que exigir el mismo día
 * descarta la vigencia sin perder ninguna franja real.
 */
function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** `Date` usable, o `undefined` si el valor no parsea. */
function toDate(value: Date | string | undefined): Date | undefined {
  if (value === undefined || value === null) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Turno del día declarado en el itinerario de la sub-ruta, si es usable. */
function shiftFromItinerary(
  subRoute: SubRouteAttributes | undefined,
  weekday: number,
): Shift | undefined {
  const entries = (subRoute?.itinerary ?? []) as unknown as ItineraryLike[];
  for (const entry of entries) {
    if (!appliesTo(entry.days, weekday)) continue;
    const start = toDate(entry.datetimeStart);
    const end = toDate(entry.datetimeEnd);
    // Los dos extremos el mismo día: si abarcan días distintos, el campo lleva una
    // vigencia y no una jornada, y tomarlo como franja es inventar un dato.
    if (start === undefined || end === undefined || !sameCalendarDay(start, end)) continue;
    const range = validRange(toHhMm(start), toHhMm(end));
    if (range) return range;
  }
  return undefined;
}

/** Ventana de entrega del día declarada en la sub-ruta, si es usable. */
function windowFromSubRoute(
  subRoute: SubRouteAttributes | undefined,
  weekday: number,
): Shift | undefined {
  const windows = (subRoute?.orderDeliveryWindows ?? []) as unknown as DeliveryWindowLike[];
  for (const w of windows) {
    if (!appliesTo(w.days, weekday)) continue;
    const range = validRange(
      composeHhMm(w.hourStart, w.minutesStart),
      composeHhMm(w.hourEnd, w.minutesEnd),
    );
    if (range) return range;
  }
  return undefined;
}

/**
 * La flota REAL de un territorio, traducida al `trucks[]` del contrato.
 *
 * Reemplaza a `resolveGroupFleet`, que devolvía **un** camión y lo inventaba
 * cuando el maestro no lo declaraba. Ese camión sintético es el origen de las 21
 * rutas con 2 camiones asignados: cada grupo de pedidos pedía uno y siempre había
 * uno disponible. Acá la flota se declara o no existe — `trucks: []` significa que
 * el territorio no se planifica, y es un resultado válido.
 *
 * Cada camión lleva la jornada, el multi-viaje y la capacidad de **su propia**
 * sub-ruta. Dos camiones del mismo territorio pueden tener jornadas distintas, y
 * el contrato lo admite: `shift` es por camión dentro de `trucks[]`.
 *
 * Una capacidad que el maestro no declara sí cae al respaldo — el camión existe y
 * tiene que poder salir; lo que no se inventa es el CAMIÓN. La procedencia viaja
 * en `capacitySource` para que la pantalla lo pueda señalar.
 */
export function resolveTerritoryFleet(
  input: ResolveTerritoryFleetInput,
): ResolvedTerritoryFleet {
  const { territoryId, territoryName, units, weekday, fallback } = input;
  const warnings: string[] = [];

  // La franja de respaldo se valida una vez para todo el territorio: viene de dos
  // inputs de pantalla y una franja invertida haría fallar la petición con `422`.
  const fallbackShift = validRange(fallback.shift?.start, fallback.shift?.end);
  if (fallbackShift === undefined) {
    warnings.push(
      `La franja configurada (${fallback.shift?.start ?? '—'}–${fallback.shift?.end ?? '—'}) no es válida: ` +
        `se usa ${SAFE_SHIFT.start}–${SAFE_SHIFT.end}.`,
    );
  }

  const trucks: ResolvedTruck[] = [];
  const seen = new Set<string>();
  const windows: Shift[] = [];

  for (const unit of units ?? []) {
    const subRoute = unit?.subRoute;
    const vehicle = (unit?.vehicle ?? (subRoute as unknown as { vehicle?: VehicleLike })?.vehicle) as
      | VehicleLike
      | undefined;
    const vehicleId = vehicle?._id === undefined || vehicle?._id === null ? '' : String(vehicle._id);
    if (!vehicleId) continue;
    // La MISMA placa declarada por dos sub-rutas del territorio es UN camión: dos
    // entradas en `trucks[]` con el mismo id le darían al motor dos vehículos que
    // en el patio son uno.
    if (seen.has(vehicleId)) {
      const other = shiftFromItinerary(subRoute, weekday);
      if (other && trucks.some((t) => t.truck.id === vehicleId && t.truck.shift.start !== other.start)) {
        warnings.push(
          `El camión ${vehicleId} está declarado por varias rutas de ${territoryName} con jornadas ` +
            `distintas: se usa la de la primera.`,
        );
      }
      continue;
    }
    seen.add(vehicleId);

    const masterCapacity = capacityLbOf(vehicle);
    const masterShift = shiftFromItinerary(subRoute, weekday);
    const shift = masterShift ?? fallbackShift ?? SAFE_SHIFT;

    const truck: Truck = {
      id: vehicleId,
      capacity: masterCapacity ?? fallback.capacityLb,
      shift,
    };

    // Multi-viaje: lo HABILITA el maestro de SU sub-ruta, y la corrida lo activa.
    //
    // El silencio del maestro NO es permiso. Lo que la habilitación afirma no es
    // tiempo —el motor modela el regreso al almacén, cobra el `reload_min` y no arma
    // un viaje que no entre en la jornada— sino que esa ruta tiene STOCK y ANDÉN para
    // una segunda carga: un hecho de la operación que no se puede deducir.
    //
    // OJO, y esto es lo que hay que saber antes de tocar esto: NINGUNA pantalla de
    // `src/` escribe `allowMultiTrips`. Se lee acá y se pide en el `select([...])` de
    // `findForPlanning`, pero no hay control que la ponga. O sea que en la práctica
    // esta rama NO SE ALCANZA, y por eso el paso de selección ya no ofrece un control
    // de viajes: un control cuyo efecto depende de una bandera que nadie puede poner
    // es una pista falsa. Ver el cambio `hide-reload-control`.
    //
    // Se probó invertirla a `!== false` —el maestro pasaría de habilitar a solo poder
    // vetar— y se descartó: mover quién decide para hacer funcionar un control es un
    // precio alto, y el stock y el andén seguían sin verificarse. Para devolver el
    // multi-viaje de verdad, el camino es un control en el maestro de sub-rutas.
    const allowsMultiTrips = (subRoute as unknown as { allowMultiTrips?: boolean } | undefined)
      ?.allowMultiTrips;
    const wantsReload = fallback.tripMode === 'reload' && fallback.maxTrips > 1;
    let tripsSource: FleetSource = 'fallback';
    if (allowsMultiTrips === true && wantsReload) {
      const reloadTotal = fallback.reloadMin * (fallback.maxTrips - 1);
      if (reloadTotal >= shiftMinutes(shift)) {
        warnings.push(
          `Las recargas (${fallback.maxTrips - 1} × ${fallback.reloadMin} min = ${reloadTotal} min) ` +
            `agotan la franja del camión ${vehicleId} (${shiftMinutes(shift)} min): un solo viaje.`,
        );
      } else {
        truck.max_trips = fallback.maxTrips;
        truck.reload_min = fallback.reloadMin;
        tripsSource = 'master';
      }
    }

    if (masterCapacity === undefined) {
      warnings.push(
        `El camión ${vehicleId} no declara una capacidad usable en el maestro: se planifica con ` +
          `${fallback.capacityLb} lb.`,
      );
    }

    trucks.push({
      truck,
      subRouteId:
        (subRoute as unknown as { _id?: unknown })?._id === undefined
          ? undefined
          : String((subRoute as unknown as { _id?: unknown })._id),
      capacitySource: masterCapacity !== undefined ? 'master' : 'fallback',
      shiftSource: masterShift !== undefined ? 'master' : 'fallback',
      tripsSource,
    });

    const window = windowFromSubRoute(subRoute, weekday);
    if (window) windows.push(window);
  }

  // Ventana común: solo si todas las declaradas coinciden. Con ventanas distintas
  // no se afirma ninguna — el pedido lo puede servir cualquiera de los camiones.
  let window: Shift | undefined;
  if (windows.length) {
    const first = windows[0];
    const allEqual = windows.every((w) => w.start === first.start && w.end === first.end);
    if (allEqual) window = first;
    else {
      warnings.push(
        `Las rutas de ${territoryName} declaran ventanas de entrega distintas: no se aplica ninguna.`,
      );
    }
  }

  return { territoryId, territoryName, trucks, window, warnings };
}
